import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import { CUSTOMER_IMPORT_FIELDS, CUSTOMER_IMPORT_LABELS, CUSTOMER_STATUS_LABELS, type CategoryDto, type CustomerDto, type CustomerGroupDto, type CustomerImportDto, type CustomerListItemDto, type ShopDto, type ViesResultDto } from '@thebb/shared';
import { del, get, post, put, upload } from '../api';
import { useAuth } from '../auth';
import { FinanceCustomerPicker } from '../components/FinanceCustomerPicker';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';
import { MoneyInput, kr } from './Catalogue';

type Page = { items: CustomerListItemDto[]; total: number; page: number; perPage: number };
const statusTone = (s: string) => (s === 'active' ? 'green' : s === 'pending' ? 'amber' : s === 'disabled' ? 'red' : 'blue') as 'green';

/** CUS-01 to CUS-11: storefront customers per shop, groups, imports, invitations and links. */
export function CustomersPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const editable = can('customers.edit');
  const shops = useQuery({ queryKey: ['shops'], queryFn: () => get<ShopDto[]>('/shops') });
  const [shopId, setShopId] = useState(() => new URLSearchParams(window.location.search).get('shopId') ?? '');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [groupId, setGroupId] = useState('');
  const [noOrders, setNoOrders] = useState(false);
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<string[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const params = new URLSearchParams({ page: String(page), perPage: '50' });
  if (shopId) params.set('shopId', shopId);
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  if (groupId) params.set('groupId', groupId);
  if (noOrders) params.set('noOrders', 'true');
  const list = useQuery({ queryKey: ['customers', params.toString()], queryFn: () => get<Page>(`/customers?${params}`) });
  const groups = useQuery({ queryKey: ['customer-groups', shopId], queryFn: () => get<CustomerGroupDto[]>(`/customers/groups/${shopId}`), enabled: !!shopId });
  const bulk = useMutation({
    mutationFn: (action: string) => post<{ done: number; skipped: number }>('/customers/bulk', { ids: picked, action }),
    onSuccess: (r) => { setNotice(`${r.done} kunder opdateret${r.skipped ? `, ${r.skipped} sprunget over` : ''}`); setPicked([]); void qc.invalidateQueries({ queryKey: ['customers'] }); },
    onError: setError,
  });
  const reset = () => setPage(1);
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.perPage)) : 1;
  const items = list.data?.items ?? [];

  return (
    <div className="space-y-4">
      <PageTitle
        title="Kunder"
        subtitle="Webshop-kunder: én konto pr. shop. Login med e-mail eller gavekode."
        actions={
          <>
            {can('customers.import') && <button className="btn" disabled={!shopId} title={shopId ? '' : 'Vælg en shop først'} onClick={() => setImporting(true)}><Upload className="h-4 w-4" /> Importér</button>}
            {editable && <button className="btn btn-primary" disabled={!shopId} title={shopId ? '' : 'Vælg en shop først'} onClick={() => setOpen('new')}>Ny kunde</button>}
          </>
        }
      />
      <div className="flex flex-wrap gap-2">
        <select className="input w-64" value={shopId} onChange={(e) => { setShopId(e.target.value); setGroupId(''); reset(); }}>
          <option value="">Alle shops</option>
          {shops.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input className="input w-64" placeholder="Søg navn, e-mail, firma, kode" value={q} onChange={(e) => { setQ(e.target.value); reset(); }} />
        <select className="input w-44" value={status} onChange={(e) => { setStatus(e.target.value); reset(); }}>
          <option value="">Alle statusser</option>
          {Object.entries(CUSTOMER_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {shopId && (
          <select className="input w-44" value={groupId} onChange={(e) => { setGroupId(e.target.value); reset(); }}>
            <option value="">Alle grupper</option>
            {groups.data?.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={noOrders} onChange={(e) => { setNoOrders(e.target.checked); reset(); }} /> Uden ordrer</label>
      </div>
      <ErrorBox error={error ?? list.error} />
      {notice && <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</div>}
      {editable && picked.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{picked.length} valgt:</span>
          <button className="btn" onClick={() => bulk.mutate('approve')}>Godkend</button>
          <button className="btn" onClick={() => bulk.mutate('invite')}>Send invitation</button>
          <button className="btn" onClick={() => bulk.mutate('disable')}>Spær</button>
          <button className="btn" onClick={() => bulk.mutate('enable')}>Genåbn</button>
          <button className="btn btn-danger" onClick={() => confirm(`Slet ${picked.length} kunder? Kunder med ordrer bliver ikke slettet.`) && bulk.mutate('delete')}>Slet</button>
        </div>
      )}
      {list.isLoading ? <Spinner /> : (
        <div className="card p-0">
          <table className="table text-sm">
            <thead>
              <tr>
                <th className="w-8"><input type="checkbox" checked={items.length > 0 && picked.length === items.length} onChange={(e) => setPicked(e.target.checked ? items.map((x) => x.id) : [])} /></th>
                <th>Kunde</th><th>Login</th>{!shopId && <th>Shop</th>}<th>Firma / afdeling</th><th>Grupper</th><th>Status</th><th className="text-right">Ordrer</th><th>Sidst logget ind</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td><input type="checkbox" checked={picked.includes(c.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, c.id] : picked.filter((x) => x !== c.id))} /></td>
                  <td><button className="font-medium text-primary hover:underline" onClick={() => setOpen(c.id)}>{c.name || '(uden navn)'}</button></td>
                  <td className="text-xs">{c.email ?? <span className="font-mono">{c.giftCode}</span>}{!c.hasPassword && c.status !== 'invited' && <div className="text-muted">ingen adgangskode</div>}</td>
                  {!shopId && <td className="text-xs">{c.shopName}</td>}
                  <td className="text-xs">{[c.company, c.department].filter(Boolean).join(' · ')}</td>
                  <td className="text-xs">{c.groupNames.join(', ')}</td>
                  <td><Badge tone={statusTone(c.status)}>{CUSTOMER_STATUS_LABELS[c.status]}</Badge></td>
                  <td className="text-right tabular-nums">{c.orderCount}</td>
                  <td className="text-xs">{c.lastLoginAt ? fmtTime(c.lastLoginAt) : '–'}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={9} className="text-muted">Ingen kunder</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></button>
          Side {page} af {pages} ({list.data?.total} kunder)
          <button className="btn" disabled={page >= pages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
      {shopId && <GroupsCard shopId={shopId} editable={editable} onError={setError} />}
      {open && <CustomerModal id={open === 'new' ? null : open} shopId={shopId} onClose={() => setOpen(null)} />}
      {importing && shopId && <ImportModal shopId={shopId} groups={groups.data ?? []} onClose={() => { setImporting(false); void qc.invalidateQueries({ queryKey: ['customers'] }); void qc.invalidateQueries({ queryKey: ['customer-groups'] }); }} />}
    </div>
  );
}

// --- one customer -----------------------------------------------------------------------------------

type Form = Omit<CustomerDto, 'id' | 'shopName' | 'hasPassword' | 'passwordAlgo' | 'vatCheckedAt' | 'externalId' | 'invitedAt' | 'approvedAt' | 'lastLoginAt' | 'createdAt' | 'orders'> & { password: string };

function CustomerModal({ id, shopId, onClose }: { id: string | null; shopId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const editable = can('customers.edit');
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [vies, setVies] = useState<ViesResultDto | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const detail = useQuery({ queryKey: ['customer', id], queryFn: () => get<CustomerDto>(`/customers/${id}`), enabled: !!id });
  const sid = detail.data?.shopId ?? shopId;
  const groups = useQuery({ queryKey: ['customer-groups', sid], queryFn: () => get<CustomerGroupDto[]>(`/customers/groups/${sid}`), enabled: !!sid });
  useEffect(() => {
    if (!id) setForm({ shopId, email: '', giftCode: null, status: 'active', name: '', company: null, phone: null, street1: null, street2: null, zip: null, city: null, countryCode: 'DK', ean: null, vatNumber: null, vatValid: null, vatOverride: false, department: null, budget: null, customFields: {}, consentMarketing: false, notes: null, paymentTermsDays: null, creditLimitMinor: null, financeCustomerNumber: null, demo: false, language: null, groupIds: [], password: '' });
    else if (detail.data) {
      const { id: _i, shopName: _s, hasPassword: _h, passwordAlgo: _p, vatCheckedAt: _v, externalId: _e, invitedAt: _iv, approvedAt: _a, lastLoginAt: _l, createdAt: _c, orders: _o, ...rest } = detail.data;
      setForm({ ...rest, password: '' });
    }
  }, [id, detail.data, shopId]);
  const done = (d: CustomerDto) => { qc.setQueryData(['customer', d.id], d); void qc.invalidateQueries({ queryKey: ['customers'] }); };
  const save = useMutation({
    mutationFn: (f: Form) => { const body = { ...f, email: f.email || null, giftCode: f.giftCode || null, password: f.password || null }; return id ? put<CustomerDto>(`/customers/${id}`, body) : post<CustomerDto>('/customers', body); },
    onSuccess: (d) => { done(d); onClose(); },
    onError: setError,
  });
  const remove = useMutation({
    mutationFn: () => del(`/customers/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['customers'] }); onClose(); },
    onError: setError,
  });
  const action = useMutation({
    mutationFn: async (a: 'invite' | 'reset' | 'link' | 'vies') => {
      if (a === 'invite') { done(await post<CustomerDto>(`/customers/${id}/invite`)); setMsg('Invitation sendt'); }
      if (a === 'reset') { await post(`/customers/${id}/reset`); setMsg('Mail med nyt adgangskode-link sendt'); }
      if (a === 'link') setLink((await post<{ url: string }>(`/customers/${id}/login-link`, { days: 7 })).url);
      if (a === 'vies') { const r = await post<ViesResultDto>(`/customers/${id}/vies`); setVies(r); void detail.refetch(); }
    },
    onSuccess: () => setError(null),
    onError: setError,
  });
  if (!form) return <Modal title="Kunde" onClose={onClose}>{detail.error ? <ErrorBox error={detail.error} /> : <Spinner />}</Modal>;
  const set = (p: Partial<Form>) => setForm({ ...form, ...p });
  const txt = (k: keyof Form, label: string, cls = '') => (
    <label className={`block ${cls}`}><span className="label">{label}</span><input className="input" value={(form[k] as string | null) ?? ''} onChange={(e) => set({ [k]: e.target.value || null } as Partial<Form>)} /></label>
  );
  const d = detail.data;
  return (
    <Modal title={id ? form.name || form.email || 'Kunde' : 'Ny kunde'} onClose={onClose}>
      <div className="max-h-[75vh] space-y-3 overflow-y-auto pr-1 text-sm">
        <ErrorBox error={error} />
        {msg && <div className="rounded bg-green-50 px-2 py-1 text-green-800">{msg}</div>}
        {d && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <Badge tone={statusTone(d.status)}>{CUSTOMER_STATUS_LABELS[d.status]}</Badge>
            {d.shopName} · oprettet {fmtTime(d.createdAt)}{d.lastLoginAt ? ` · sidst inde ${fmtTime(d.lastLoginAt)}` : ''}{d.passwordAlgo === 'bcrypt' ? ' · adgangskode fra 3x' : ''}{!d.hasPassword ? ' · ingen adgangskode' : ''}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <label className="block"><span className="label">E-mail</span><input className="input" value={form.email ?? ''} onChange={(e) => set({ email: e.target.value })} /></label>
          <label className="block"><span className="label">Brugernavn (medarbejdernr., gavekode eller andet)</span><input className="input font-mono" value={form.giftCode ?? ''} onChange={(e) => set({ giftCode: e.target.value || null })} /></label>
          {txt('name', 'Navn')}
          <label className="block"><span className="label">{d?.hasPassword ? 'Ny adgangskode (tom: uændret)' : 'Adgangskode (tom: send invitation)'}</span><input className="input" type="password" autoComplete="new-password" value={form.password} onChange={(e) => set({ password: e.target.value })} /></label>
          {txt('company', 'Firma')}
          {txt('phone', 'Telefon')}
          {txt('street1', 'Adresse')}
          {txt('street2', 'Adresse 2')}
          {txt('zip', 'Postnr.')}
          {txt('city', 'By')}
          {txt('countryCode', 'Land')}
          {txt('ean', 'EAN')}
          <div>
            {txt('vatNumber', 'CVR / momsnummer')}
            <div className="mt-1 flex items-center gap-2 text-xs">
              {form.vatNumber && (form.vatValid === true ? <Badge tone="green">gyldigt{form.vatOverride ? ' (manuelt)' : ''}</Badge> : form.vatValid === false ? <Badge tone="red">ugyldigt i VIES</Badge> : <Badge>ikke tjekket</Badge>)}
              {id && form.vatNumber && editable && <button className="text-primary hover:underline" onClick={() => action.mutate('vies')}>Tjek i VIES</button>}
              {form.vatNumber && editable && <label className="flex items-center gap-1"><input type="checkbox" checked={form.vatOverride} onChange={(e) => set({ vatOverride: e.target.checked, vatValid: e.target.checked ? true : null })} /> godkend manuelt</label>}
            </div>
            {vies && <div className="text-xs text-muted">{vies.name}{vies.address ? `, ${vies.address}` : ''}</div>}
          </div>
          {txt('department', 'Afdeling')}
          {txt('budget', 'Budget')}
          <label className="block">
            <span className="label">Status</span>
            <select className="input" value={form.status} onChange={(e) => set({ status: e.target.value as Form['status'] })}>
              {Object.entries(CUSTOMER_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="label">Sprog</span>
            <select className="input" value={form.language ?? ''} onChange={(e) => set({ language: (e.target.value || null) as Form['language'] })}>
              <option value="">Shoppens</option><option value="da">Dansk</option><option value="en">Engelsk</option>
            </select>
          </label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="block"><span className="label">Betalingsfrist (dage)</span><input className="input" type="number" min={0} value={form.paymentTermsDays ?? ''} onChange={(e) => set({ paymentTermsDays: e.target.value === '' ? null : Number(e.target.value) })} /></label>
          <label className="block"><span className="label">Kreditgrænse (kr.)</span><MoneyInput className="input" value={form.creditLimitMinor} onChange={(creditLimitMinor) => set({ creditLimitMinor })} /></label>
        </div>
        <FinanceCustomerPicker value={form.financeCustomerNumber ?? null} onChange={(financeCustomerNumber) => set({ financeCustomerNumber })} />
        <p className="-mt-1 text-xs text-muted">Kundens fakturaordrer faktureres til denne kunde under Økonomi; uden den bruges shoppens kunde og ellers faktureringsadressen.</p>
        {groups.data && groups.data.length > 0 && (
          <div>
            <div className="label">Grupper</div>
            {groups.data.map((g) => <label key={g.id} className="mr-4 inline-flex items-center gap-1"><input type="checkbox" checked={form.groupIds.includes(g.id)} onChange={(e) => set({ groupIds: e.target.checked ? [...form.groupIds, g.id] : form.groupIds.filter((x) => x !== g.id) })} /> {g.name}</label>)}
          </div>
        )}
        {Object.keys(form.customFields).length > 0 && (
          <div className="text-xs"><div className="label">Egne felter</div>{Object.entries(form.customFields).map(([k, v]) => <div key={k}><span className="text-muted">{k}:</span> {v}</div>)}</div>
        )}
        <label className="block"><span className="label">Note (kun internt)</span><textarea className="input" rows={2} value={form.notes ?? ''} onChange={(e) => set({ notes: e.target.value || null })} /></label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.consentMarketing} onChange={(e) => set({ consentMarketing: e.target.checked })} /> Samtykke til nyhedsmails</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.demo} onChange={(e) => set({ demo: e.target.checked })} /> Demo-login (salg)</label>
        </div>
        {d && d.orders.length > 0 && (
          <div>
            <div className="label">Ordrer</div>
            {d.orders.map((o) => <div key={o.id} className="flex justify-between"><Link to="/orders/$id" params={{ id: o.id }} className="text-primary hover:underline">{o.number}</Link><span>{o.status}</span><span className="tabular-nums">{kr(o.totalMinor)}</span></div>)}
          </div>
        )}
        {link && <div className="rounded bg-canvas p-2 text-xs"><div className="label">Login-link (7 dage, én gang)</div><input className="input font-mono text-xs" readOnly value={link} onFocus={(e) => e.target.select()} /></div>}
        <div className="flex flex-wrap justify-between gap-2 border-t border-line pt-3">
          <div className="flex flex-wrap gap-2">
            {id && editable && d?.email && <button className="btn" onClick={() => action.mutate('invite')}>{d.invitedAt ? 'Send invitation igen' : 'Send invitation'}</button>}
            {id && editable && d?.email && d.hasPassword && <button className="btn" onClick={() => action.mutate('reset')}>Nulstil adgangskode</button>}
            {id && editable && <button className="btn" onClick={() => action.mutate('link')}>Login-link</button>}
          </div>
          <div className="flex gap-2">
            {id && editable && <button className="btn btn-danger" disabled={remove.isPending} onClick={() => confirm('Slet kunden? Det kan ikke fortrydes.') && remove.mutate()}>Slet</button>}
            {editable && <button className="btn btn-primary" disabled={save.isPending || (!form.email && !form.giftCode)} onClick={() => save.mutate(form)}>Gem</button>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// --- groups -----------------------------------------------------------------------------------------

function GroupsCard({ shopId, editable, onError }: { shopId: string; editable: boolean; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const groups = useQuery({ queryKey: ['customer-groups', shopId], queryFn: () => get<CustomerGroupDto[]>(`/customers/groups/${shopId}`) });
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => get<{ items: CategoryDto[] }>('/catalogue/categories') });
  const [name, setName] = useState('');
  const [edit, setEdit] = useState<CustomerGroupDto | null>(null);
  const set = (d: CustomerGroupDto[]) => { qc.setQueryData(['customer-groups', shopId], d); onError(null); };
  const add = useMutation({ mutationFn: () => post<CustomerGroupDto[]>(`/customers/groups/${shopId}`, { name, categoryIds: [] }), onSuccess: (d) => { set(d); setName(''); }, onError });
  const save = useMutation({ mutationFn: (g: CustomerGroupDto) => put<CustomerGroupDto[]>(`/customers/groups/${shopId}/${g.id}`, { name: g.name, categoryIds: g.categoryIds }), onSuccess: (d) => { set(d); setEdit(null); }, onError });
  const remove = useMutation({ mutationFn: (id: string) => del<CustomerGroupDto[]>(`/customers/groups/${shopId}/${id}`), onSuccess: (d) => { set(d); setEdit(null); }, onError });
  const catName = (id: string) => cats.data?.items.find((c) => c.id === id)?.name.da ?? '?';
  return (
    <div className="card max-w-3xl space-y-2 text-sm">
      <div className="label">Kundegrupper i shoppen</div>
      <p className="text-xs text-muted">En kategori, der er knyttet til en eller flere grupper, vises kun for kunder i de grupper.</p>
      {groups.data?.map((g) => (
        <div key={g.id} className="flex items-center justify-between border-b border-line py-1">
          <button className="font-medium text-primary hover:underline" disabled={!editable} onClick={() => setEdit(g)}>{g.name}</button>
          <span className="text-xs text-muted">{g.memberCount} kunder{g.categoryIds.length ? ` · ser: ${g.categoryIds.map(catName).join(', ')}` : ''}</span>
        </div>
      ))}
      {editable && (
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (name.trim()) add.mutate(); }}>
          <input className="input w-64" placeholder="Ny gruppe" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn">Tilføj</button>
        </form>
      )}
      {edit && (
        <Modal title={`Gruppe: ${edit.name}`} onClose={() => setEdit(null)}>
          <div className="space-y-2 text-sm">
            <input className="input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
            <div className="label">Kategorier kun for denne gruppe</div>
            <div className="max-h-72 overflow-y-auto">
              {cats.data?.items.map((c) => <label key={c.id} className="flex items-center gap-2" style={{ paddingLeft: c.parentId ? 16 : 0 }}><input type="checkbox" checked={edit.categoryIds.includes(c.id)} onChange={(e) => setEdit({ ...edit, categoryIds: e.target.checked ? [...edit.categoryIds, c.id] : edit.categoryIds.filter((x) => x !== c.id) })} /> {c.name.da || c.slug}</label>)}
            </div>
            <div className="flex justify-between">
              <button className="btn btn-danger" onClick={() => confirm('Slet gruppen?') && remove.mutate(edit.id)}>Slet</button>
              <button className="btn btn-primary" onClick={() => save.mutate(edit)}>Gem</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// --- import -----------------------------------------------------------------------------------------

function ImportModal({ shopId, groups, onClose }: { shopId: string; groups: CustomerGroupDto[]; onClose: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<unknown>(null);
  const [imp, setImp] = useState<CustomerImportDto | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [invite, setInvite] = useState(true);
  const [groupId, setGroupId] = useState('');
  const [busy, setBusy] = useState(false);
  const running = imp && (imp.status === 'queued' || imp.status === 'running');
  useEffect(() => {
    if (!running) return;
    const t = setInterval(async () => setImp(await get<CustomerImportDto>(`/customers/import/${imp.id}`)), 1500);
    return () => clearInterval(t);
  }, [running, imp?.id]);
  const start = async () => {
    setBusy(true);
    try {
      setImp(await post<CustomerImportDto>(`/customers/import/${imp!.id}/start`, { mapping, invite, groupId: groupId || null }));
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Importér kunder" onClose={onClose}>
      <div className="max-h-[75vh] space-y-3 overflow-y-auto text-sm">
        <ErrorBox error={error} />
        {!imp && (
          <>
            <p className="text-muted">CSV (semikolon, komma eller tab) eller XLSX. Første linje er overskrifter. Kunder matches på eksternt id, e-mail eller gavekode og opdateres, hvis de findes.</p>
            <input ref={ref} type="file" accept=".csv,.txt,.xlsx" onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setBusy(true);
              try { const r = await upload<CustomerImportDto>(`/customers/imports/${shopId}`, f); setImp(r); setMapping(r.mapping); setError(null); } catch (err) { setError(err); } finally { setBusy(false); }
            }} />
            {busy && <Spinner />}
          </>
        )}
        {imp && imp.status === 'uploaded' && (
          <>
            <div className="text-muted">{imp.fileName}: {imp.rows} rækker. Vælg hvad hver kolonne er.</div>
            <table className="table text-xs">
              <thead><tr><th>Kolonne</th><th>Eksempel</th><th>Felt</th></tr></thead>
              <tbody>
                {imp.headers.map((h, i) => (
                  <tr key={i}>
                    <td className="font-medium">{h || `(kolonne ${i + 1})`}</td>
                    <td className="max-w-48 truncate text-muted">{imp.sample.map((r) => r[i]).filter(Boolean).slice(0, 2).join(' · ')}</td>
                    <td>
                      <select className="input" value={mapping[String(i)] ?? ''} onChange={(e) => { const m = { ...mapping }; if (e.target.value) m[String(i)] = e.target.value; else delete m[String(i)]; setMapping(m); }}>
                        <option value="">Ignorér</option>
                        {CUSTOMER_IMPORT_FIELDS.map((f) => <option key={f} value={f}>{CUSTOMER_IMPORT_LABELS[f]}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <label className="flex items-center gap-2"><input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} /> Send invitation til nye kunder med e-mail og uden adgangskode</label>
            {groups.length > 0 && (
              <label className="block"><span className="label">Sæt alle i gruppe</span>
                <select className="input" value={groupId} onChange={(e) => setGroupId(e.target.value)}><option value="">Ingen</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>
              </label>
            )}
            <div className="flex justify-end"><button className="btn btn-primary" disabled={busy || !Object.values(mapping).some((v) => v === 'email' || v === 'giftCode')} onClick={start}>Start import</button></div>
          </>
        )}
        {imp && imp.status !== 'uploaded' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">{running && <Spinner />}<Badge tone={imp.status === 'done' ? 'green' : imp.status === 'failed' ? 'red' : 'blue'}>{imp.status === 'done' ? 'Færdig' : imp.status === 'failed' ? 'Fejlede' : 'Kører'}</Badge> {imp.fileName}</div>
            <div>Oprettet {imp.created} · opdateret {imp.updated} · sprunget over {imp.skipped} af {imp.rows}</div>
            {imp.errors.length > 0 && <div className="max-h-48 overflow-y-auto rounded bg-red-50 p-2 text-xs text-red-800">{imp.errors.map((e, i) => <div key={i}>{e.row ? `Række ${e.row}: ` : ''}{e.message}</div>)}</div>}
            {!running && <div className="flex justify-end"><button className="btn" onClick={onClose}>Luk</button></div>}
          </div>
        )}
      </div>
    </Modal>
  );
}
